import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  mvpApiGetStoreOptions,
  mvpApiGetStoresInventoryOptions,
} from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { mvpApiListStoresOptions as atlasListStoresOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import {
  mvpApiListStores,
  mvpApiListCvUpdateRequests,
  mvpApiListCvUpdateResponses,
} from "@labeling-tools/api/lebron/generated";
import type {
  StoreSchema as LebronStore,
  RetailerInventorySchema,
  CvUpdateRequestSchema,
  CvUpdateResponseSchema,
} from "@labeling-tools/api/lebron/generated/types.gen";
import type { StoreSchema as AtlasStore } from "@labeling-tools/api/atlas/types.gen";

export type {
  LebronStore,
  AtlasStore,
  RetailerInventorySchema,
  CvUpdateRequestSchema,
  CvUpdateResponseSchema,
};

/**
 * A store record minus its own `active` flag.
 *
 * `GET /v1/stores` grew an optional `active` after this app was written, but
 * the app's three-valued one — true / false / inventory never resolved it —
 * is the one every view filters on, and `boolean | undefined` cannot express
 * the third case. Anything that overlays or consumes a joined store builds on
 * this so the two never collide.
 */
export type StoreRecord = Omit<LebronStore, "active">;

/** Well under Lebron's advertised 10 000 cap, so the server cannot clamp it. */
const STORE_PAGE_SIZE = 1000;

export type AllStores = {
  stores: LebronStore[];
  /** The server's own total, for cross-checking against `stores.length`. */
  count: number;
  pagesFetched: number;
};

/**
 * Lebron holds the operational record — hours, timezone, OOS batch cadence.
 *
 * Walks every page rather than assuming one oversized request covers the
 * fleet: a server-side clamp on `page_size` would otherwise silently truncate
 * the list with nothing in the UI to indicate stores were missing.
 */
export function useStores() {
  return useQuery({
    queryKey: ["lebron", "stores", "all", STORE_PAGE_SIZE],
    queryFn: async (): Promise<AllStores> => {
      const first = await mvpApiListStores({
        query: { page: 1, page_size: STORE_PAGE_SIZE, ordering: "name" },
        throwOnError: true,
      });

      const stores = [...first.data.stores];
      const totalPages = first.data.total_pages;

      for (let page = 2; page <= totalPages; page++) {
        const next = await mvpApiListStores({
          query: { page, page_size: STORE_PAGE_SIZE, ordering: "name" },
          throwOnError: true,
        });
        stores.push(...next.data.stores);
      }

      return {
        stores,
        count: first.data.count,
        pagesFetched: Math.max(1, totalPages),
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useStore(storeId: string | undefined) {
  return useQuery({
    ...mvpApiGetStoreOptions({ path: { store_id: storeId ?? "" } }),
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Store inventory carries the fields the plain store record leaves out —
 * `active`, `tags`, and the resolved retailer — plus per-store device counts.
 */
export function useStoreInventory() {
  return useQuery({
    ...mvpApiGetStoresInventoryOptions(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Atlas is the catalogue-side record for the same store, and the only source of
 * `address` and `locale_web`. Looked up by `lebron_id` one store at a time —
 * Atlas caps `page_size` at 1000, so fetching the whole set is not safe here.
 */
export function useAtlasStoreByLebronId(lebronId: string | undefined) {
  return useQuery({
    ...atlasListStoresOptions({ query: { lebron_id: lebronId ?? "" } }),
    enabled: Boolean(lebronId),
    staleTime: 15 * 60 * 1000,
    select: (data): AtlasStore | undefined => data.stores[0],
  });
}

/**
 * A store plus the fields only the inventory endpoint resolves.
 *
 * `retailer` and `active` are nullable because inventory is optional
 * enrichment — if that request fails the table still renders, just without
 * those columns. See `StoreRecord` for why `active` is replaced, not merged.
 */
export interface StoreWithMeta extends StoreRecord {
  retailer: RetailerInventorySchema | null;
  active: boolean | null;
}

export interface AllStoresWithMeta {
  stores: StoreWithMeta[];
  count: number;
  pagesFetched: number;
}

/**
 * The store list both views work from.
 *
 * Joins `GET /v1/stores` to `GET /v1/inventory/stores` on store id, because
 * the plain store record carries only a `retailer_id` external code — 206
 * distinct values across 207 stores, so effectively unique per store and
 * useless for grouping. The retailer's actual name and short name come from
 * inventory.
 */
export function useStoresWithMeta(): {
  data: AllStoresWithMeta | undefined;
  isLoading: boolean;
  error: unknown;
  /** True when retailer/active could not be loaded, so filters lack options. */
  metaUnavailable: boolean;
} {
  const stores = useStores();
  const inventory = useStoreInventory();

  const data = useMemo<AllStoresWithMeta | undefined>(() => {
    if (!stores.data) return undefined;

    const byId = new Map(
      (inventory.data ?? []).map((entry) => [entry.id, entry]),
    );

    return {
      count: stores.data.count,
      pagesFetched: stores.data.pagesFetched,
      stores: stores.data.stores.map((store) => {
        const meta = byId.get(store.id);
        return {
          ...store,
          retailer: meta?.retailer ?? null,
          active: meta?.active ?? null,
        };
      }),
    };
  }, [stores.data, inventory.data]);

  return {
    data,
    // Inventory is enrichment, so its loading state must not gate the table.
    isLoading: stores.isLoading,
    error: stores.error,
    metaUnavailable: inventory.isError,
  };
}

/** Retailer names present in a store list, for populating a filter. */
export function retailerNames(stores: StoreWithMeta[]): string[] {
  const set = new Set<string>();
  for (const store of stores) {
    if (store.retailer?.name) set.add(store.retailer.name);
  }
  return [...set].sort();
}

/* ---------------------------------------------------------------------------
 * CV deployments
 * ------------------------------------------------------------------------ */

/** Lebron allows 10 000; 1000 keeps a page small enough to arrive quickly. */
const DEPLOYMENT_PAGE_SIZE = 1000;

/**
 * Every page of a Lebron list endpoint, walked rather than assumed.
 *
 * A blast records two rows per store (IN_PROGRESS then the outcome), so a
 * 260-store fleet is already past 500 rows and a single oversized request is
 * one server-side clamp away from silently truncating the deployment — which
 * would read in the UI as stores that never reported.
 */
async function fetchAllPages<T>(
  page: (pageNumber: number) => Promise<{ total_pages: number; items: T[] }>,
): Promise<T[]> {
  const first = await page(1);
  const items = [...first.items];
  for (let n = 2; n <= first.total_pages; n++) {
    items.push(...(await page(n)).items);
  }
  return items;
}

/**
 * Recent cv update requests — the deployments the picker chooses between.
 *
 * Ordered newest first so the current deploy is the default selection, and
 * bounded by `sinceDays` because the whole history is neither interesting nor
 * small.
 */
export function useCvUpdateRequests(sinceDays = 14) {
  const createdAfter = useMemo(() => {
    const from = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000);
    return from.toISOString();
  }, [sinceDays]);

  return useQuery({
    queryKey: ["lebron", "cv-update-requests", createdAfter],
    queryFn: () =>
      fetchAllPages<CvUpdateRequestSchema>(async (page) => {
        const { data } = await mvpApiListCvUpdateRequests({
          query: {
            page,
            page_size: DEPLOYMENT_PAGE_SIZE,
            ordering: "-created_at",
            created_after: createdAfter,
          },
          throwOnError: true,
        });
        return {
          total_pages: data.total_pages,
          items: data.cv_update_requests,
        };
      }),
    // Short, so a deploy started while this page is open shows up in the
    // dropdown without a reload.
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/**
 * Every response event for one deployment, polled while it is still running.
 *
 * `live` is the caller's verdict on whether the deployment has finished —
 * polling stops when it has, so a page left open on last week's deploy does not
 * keep hitting Lebron. Responses are append-only, so there is nothing to
 * invalidate: each poll simply sees more rows than the last.
 */
export function useCvUpdateResponses(
  requestId: string | null | undefined,
  live: boolean,
  intervalMs = 10 * 1000,
) {
  return useQuery({
    queryKey: ["lebron", "cv-update-responses", requestId],
    queryFn: () =>
      fetchAllPages<CvUpdateResponseSchema>(async (page) => {
        const { data } = await mvpApiListCvUpdateResponses({
          query: {
            page,
            page_size: DEPLOYMENT_PAGE_SIZE,
            ordering: "created_at",
            request_id: requestId ?? "",
          },
          throwOnError: true,
        });
        return {
          total_pages: data.total_pages,
          items: data.cv_update_responses,
        };
      }),
    enabled: Boolean(requestId),
    staleTime: 0,
    refetchInterval: live ? intervalMs : false,
    // A deploy is watched with the tab in the background as often as not.
    refetchIntervalInBackground: true,
    // Keep the last good snapshot on screen while a poll is in flight, so the
    // bar does not blink back to a loading state every interval.
    placeholderData: (previous) => previous,
  });
}
